import React, { useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import NewCustomAxioActionModal from "../../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";

const modals = {
  addConfirmation: {
    title: "Success!",
    ariaLabel: "Degree Added Successfully Modal",
    warningText: "The degree has been successfully added.",
    actionButtonText: "OK",
  },
  addError: {
    title: "Error!",
    ariaLabel: "Degree Addition Error Modal",
    warningText: "An error occurred while adding the degree. You may not have permission to add a degree.",
    actionButtonText: "CLOSE",
  },
};

export enum AddDegreeModalState {
  CLOSED = "CLOSED",
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  ADD_ERROR = "ADD_ERROR",
}

const degreeSchema = z.object({
  degreeName: z.string().min(1, "Degree name is required"),
});

type DegreeFormData = z.infer<typeof degreeSchema>;

interface AddDegreeModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: DegreeFormData) => Promise<any>;
}

const AddDegreeModal: React.FC<AddDegreeModalProps> = ({ visible, onClose, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: AddDegreeModalState.CLOSED,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
  } = useForm<DegreeFormData>({
    resolver: zodResolver(degreeSchema),
    mode: "onChange",
    defaultValues: {
      degreeName: "",
    },
  });

  const handleClose = () => {
    reset();
    setModalState({ state: AddDegreeModalState.CLOSED });
    onClose();
  };

  const handleSubmitForm = async (data: DegreeFormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(data);
      if (result?.createDegree?.success) {
        reset();
        setModalState({ state: AddDegreeModalState.ADD_CONFIRMATION });
        onClose();
      } else {
        setModalState({ state: AddDegreeModalState.ADD_ERROR });
      }
    } catch (error) {
      setModalState({ state: AddDegreeModalState.ADD_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleClose}
        title="Add a Degree"
        style={{ width: isMedium ? "30%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}
        ariaLabel="Add degree modal">
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.container}>
            <View style={styles.formGroup}>
              <CustomText
                text="Degree Name"
                size="s"
                style={{ marginBottom: 4, color: "#182230" }}
                weight="ultraBold"
              />
              <Controller
                control={control}
                rules={{ required: true }}
                name="degreeName"
                render={({ field: { onChange, value } }) => (
                  <CustomTextInput
                    testID="degree-name-input"
                    value={value}
                    onChangeText={onChange}
                    placeholder="Bachelor of Computer Science"
                    style={styles.input}
                  />
                )}
              />
              {errors?.degreeName?.message && (
                <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.degreeName?.message} />
              )}
            </View>

            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleClose}
                styleType="transparent"
                size="m"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
              />
              <CustomButton
                text="Add Degree"
                onPress={handleSubmit(handleSubmitForm)}
                styleType="primary"
                size="m"
                style={styles.addButton}
                textStyle={styles.addButtonText}
                disabled={!isValid || isSubmitting}
              />
            </View>
          </View>
        </ScrollView>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState.state === AddDegreeModalState.ADD_CONFIRMATION}
        onClose={() => setModalState({ state: AddDegreeModalState.CLOSED })}
        modalTexts={modals.addConfirmation}
        contextualText={getValues("degreeName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === AddDegreeModalState.ADD_ERROR}
        onClose={() => setModalState({ state: AddDegreeModalState.CLOSED })}
        modalTexts={modals.addError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  input: {
    width: "100%",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  addButton: {
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  addButtonText: {
    color: "#FFFFFF",
  },
});

export default AddDegreeModal;
